import type { TrackingInterval } from "../contracts";

const LOOKAHEAD_SECONDS = 1;
const URGENT_BURST = 3;

/** Unknown or constrained devices keep one compute worker and one interval's pixel reservation. */
/**
 * Never more than this, whatever the device reports.
 *
 * Measured over 143 intervals, compute is 75% of the work and decoding 25%,
 * and decoding is serialized through one worker per video. The floor is
 * therefore `max(decode, compute / workers)`: four workers reach it, and past
 * four the decode side binds, so further slots idle rather than help. Each
 * also reserves its own share of cropped pixels, so they are not free.
 */
const MAX_COMPUTE_WORKERS = 4;

/** Cores held back for the main thread and the shared decode worker. */
const RESERVED_CORES = 2;

/**
 * How many compute workers this device can carry.
 *
 * Each in-flight interval reserves `maxIntervalBytes`, and the pixel budget is
 * this count times that, so the number is a memory decision as much as a CPU
 * one.
 *
 * A touch device is capped at one worker whatever it reports. A tab there is
 * killed for holding a few hundred MB, and reported memory does not change
 * that: Android Chrome reports `deviceMemory`, so keying the cap on its
 * absence covered iOS and left an 8 GiB Android phone taking four workers and
 * a 512 MiB ceiling — the case the cap exists to prevent.
 *
 * `deviceMemory` is Chromium-only, so elsewhere its absence says nothing
 * about the device and must not be read as "constrained" — doing so pinned
 * every Safari and Firefox desktop to one worker regardless of its cores.
 */
export function trackingWorkerCount(logicalCores?: number, memoryGiB?: number, touch = false): number {
  const cores = Number.isFinite(logicalCores) ? logicalCores! : 0;
  if (touch) return 1;

  const byCores = Math.floor((cores - RESERVED_CORES) / 2);
  // Present and small is a real constraint; absent is simply unknown.
  const byMemory = Number.isFinite(memoryGiB) && memoryGiB! <= 4 ? 1 : MAX_COMPUTE_WORKERS;
  return Math.max(1, Math.min(byCores, byMemory, MAX_COMPUTE_WORKERS));
}

/** What this device reports, sampled in one place for every consumer. */
export function deviceComputeSlots(): number {
  if (typeof navigator === "undefined") return 1;
  return trackingWorkerCount(
    navigator.hardwareConcurrency,
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
    // iPadOS reports itself as a Mac, so the touch points rather than the
    // platform string identify a device with a tab memory limit.
    navigator.maxTouchPoints > 1,
  );
}

export function trackingAdmissionCapacity(
  idleSlots: number,
  reservedBytes: number,
  reservationBytes: number,
  budgetBytes: number,
): number {
  if (!(reservationBytes > 0)) return 0;
  return Math.max(0, Math.min(idleSlots, Math.floor((budgetBytes - reservedBytes) / reservationBytes)));
}

function priority(interval: TrackingInterval, time: number): number {
  if (interval.start.time <= time && interval.end.time >= time) return 0;
  if (interval.start.time > time && interval.start.time <= time + LOOKAHEAD_SECONDS) return 1;
  return 2;
}

/** Central admission queue; workers receive only the job they can start now. */
export class TrackingScheduler {
  private order = new Map<string, number>();
  private sequence = 0;
  private dispatches = 0;
  private urgentStreak = 0;
  private lastTrack = new Map<string, number>();

  takeBatch(
    intervals: readonly TrackingInterval[],
    attempted: Set<string>,
    time: number,
    capacity: number,
  ): TrackingInterval[] {
    if (capacity < 1) return [];
    const currentIds = new Set(intervals.map((interval) => interval.id));
    for (const id of this.order.keys()) if (!currentIds.has(id)) this.order.delete(id);
    for (const interval of intervals) {
      if (!this.order.has(interval.id)) this.order.set(interval.id, this.sequence++);
    }
    const batch: TrackingInterval[] = [];
    for (let slot = 0; slot < Math.min(2, capacity); slot++) {
      const waiting = intervals.filter((interval) => !attempted.has(interval.id));
      const background =
        this.urgentStreak >= URGENT_BURST ? waiting.filter((interval) => priority(interval, time) === 2) : [];
      const oldestBackground = background.length > 0;
      const candidates = oldestBackground ? background : waiting;
      candidates.sort((a, b) => {
        // Every fourth urgent admission offers the oldest background job a turn.
        // Its age survives seeks, so repeated interaction cannot starve the video.
        if (oldestBackground) return this.order.get(a.id)! - this.order.get(b.id)!;
        const aPriority = priority(a, time);
        const bPriority = priority(b, time);
        if (aPriority !== bPriority) return aPriority - bPriority;
        if (aPriority < 2) {
          const fairness = (this.lastTrack.get(a.trackId) ?? -1) - (this.lastTrack.get(b.trackId) ?? -1);
          if (fairness) return fairness;
        }
        const distance = (interval: TrackingInterval) =>
          Math.max(interval.start.time - time, time - interval.end.time, 0);
        return distance(a) - distance(b) || this.order.get(a.id)! - this.order.get(b.id)!;
      });
      const next = candidates[0];
      if (!next) break;
      // Sharing a decode must not overtake a higher-priority different window.
      const first = batch[0];
      if (first && (next.start.time !== first.start.time || next.end.time !== first.end.time)) break;
      batch.push(next);
      attempted.add(next.id);
      this.lastTrack.set(next.trackId, this.dispatches++);
      this.urgentStreak = priority(next, time) < 2 ? this.urgentStreak + 1 : 0;
    }
    return batch;
  }
}
